import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CallMadeRoundedIcon from "@mui/icons-material/CallMadeRounded";
import CallReceivedRoundedIcon from "@mui/icons-material/CallReceivedRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import {
  ACCOUNT_TYPE_LABELS,
  ERP_BRAND,
  entityId,
  formatDateTime,
  formatMoney,
  uniqueRequestKey,
} from "../../../utils/erp";

const initialRegister = { code: "", name: "", accountCode: "", currency: "ARS", branchName: "", notes: "" };
const initialOpen = { openingAmount: "0", notes: "" };
const initialClose = { declaredClosingAmount: "0", notes: "" };
function newOperationForm() {
  return { direction: "in", amount: "", description: "", counterAccountCode: "OTHER_INCOME", reference: "", notes: "", idempotencyKey: uniqueRequestKey("cash-ui") };
}

export default function ProjectErpCashPage() {
  const { projectId } = useParams();
  const { bootstrap, refreshBootstrap } = useOutletContext();
  const [registers, setRegisters] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [cashMovements, setCashMovements] = React.useState([]);
  const [movementsTotal, setMovementsTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [registerDialog, setRegisterDialog] = React.useState(false);
  const [registerForm, setRegisterForm] = React.useState(initialRegister);
  const [openTarget, setOpenTarget] = React.useState(null);
  const [openForm, setOpenForm] = React.useState(initialOpen);
  const [closeTarget, setCloseTarget] = React.useState(null);
  const [closeForm, setCloseForm] = React.useState(initialClose);
  const [operationTarget, setOperationTarget] = React.useState(null);
  const [operationForm, setOperationForm] = React.useState(newOperationForm);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [registerData, sessionData, movementData] = await Promise.all([
        erpApi.listCashRegisters(projectId),
        erpApi.listCashSessions(projectId, { page: 1, pageSize: 20 }),
        erpApi.listCashMovements(projectId, { page: page + 1, pageSize: rowsPerPage }),
      ]);
      setRegisters(registerData.registers || []);
      setSessions(sessionData.cashSessions || []);
      setCashMovements(movementData.cashMovements || []);
      setMovementsTotal(Number(movementData.total || 0));
    } catch (err) {
      setError(err.message || "No se pudo cargar el módulo de caja");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage]);

  React.useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    await Promise.all([load(), refreshBootstrap()]);
  };

  const createRegister = async () => {
    if (!registerForm.code.trim() || !registerForm.name.trim()) {
      setError("Completá el código y el nombre de la caja.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await erpApi.createCashRegister(projectId, {
        ...registerForm,
        code: registerForm.code.toUpperCase(),
        accountCode: (registerForm.accountCode || `CASH_${registerForm.code}`).toUpperCase(),
      });
      setRegisterDialog(false);
      setRegisterForm(initialRegister);
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo crear la caja");
    } finally {
      setSaving(false);
    }
  };

  const openRegister = async () => {
    try {
      setSaving(true);
      setError("");
      await erpApi.openCashRegister(projectId, entityId(openTarget?.register), {
        openingAmount: Number(openForm.openingAmount || 0),
        notes: openForm.notes,
      });
      setOpenTarget(null);
      setOpenForm(initialOpen);
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo abrir la caja");
    } finally {
      setSaving(false);
    }
  };

  const closeRegister = async () => {
    try {
      setSaving(true);
      setError("");
      await erpApi.closeCashSession(projectId, entityId(closeTarget?.openSession), {
        declaredClosingAmount: Number(closeForm.declaredClosingAmount || 0),
        notes: closeForm.notes,
      });
      setCloseTarget(null);
      setCloseForm(initialClose);
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo cerrar la caja");
    } finally {
      setSaving(false);
    }
  };

  const recordOperation = async () => {
    if (Number(operationForm.amount || 0) <= 0 || !operationForm.description.trim()) {
      setError("Completá una descripción y un importe mayor a cero.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await erpApi.recordCashOperation(projectId, {
        registerId: entityId(operationTarget?.register),
        direction: operationForm.direction,
        amount: Number(operationForm.amount),
        description: operationForm.description,
        counterAccountCode: operationForm.counterAccountCode,
        reference: operationForm.reference,
        notes: operationForm.notes,
        idempotencyKey: operationForm.idempotencyKey,
      });
      setOperationTarget(null);
      setOperationForm(newOperationForm());
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo registrar la operación de caja");
    } finally {
      setSaving(false);
    }
  };

  const incomeAccounts = (bootstrap.accounts || []).filter((account) => account.active && ["income", "liability", "equity"].includes(account.type));
  const expenseAccounts = (bootstrap.accounts || []).filter((account) => account.active && ["expense", "asset", "liability"].includes(account.type));
  const operationAccounts = operationForm.direction === "in" ? incomeAccounts : expenseAccounts;

  if (loading && !registers.length) return <ErpLoading label="Cargando cajas y arqueos..." />;

  return (
    <Stack spacing={2.5}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <ErpSectionTitle
        title="Caja y arqueos"
        description="Administrá cajas físicas, turnos, ingresos, egresos y diferencias de cierre."
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
            <Button variant="contained" startIcon={<AddBusinessRoundedIcon />} onClick={() => setRegisterDialog(true)} sx={{ textTransform: "none", fontWeight: 850 }}>Nueva caja</Button>
          </Stack>
        }
      />

      {!registers.length ? (
        <ErpPanel>
          <ErpEmpty
            title="Todavía no hay cajas configuradas"
            description="Creá una caja principal para comenzar a registrar aperturas, cobros, egresos y arqueos."
            action={<Button variant="contained" startIcon={<AddBusinessRoundedIcon />} onClick={() => setRegisterDialog(true)}>Crear caja</Button>}
          />
        </ErpPanel>
      ) : (
        <Grid container spacing={2}>
          {registers.map((item) => {
            const register = item.register;
            const isOpen = Boolean(item.openSession);
            return (
              <Grid item xs={12} md={6} xl={4} key={entityId(register)}>
                <ErpPanel sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="h6" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{register.name}</Typography>
                          <Chip size="small" color={isOpen ? "success" : "default"} label={isOpen ? "Abierta" : "Cerrada"} sx={{ fontWeight: 850 }} />
                        </Stack>
                        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>{register.code} · {register.accountCode}</Typography>
                        {register.branchName ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{register.branchName}</Typography> : null}
                      </Box>
                      <Box sx={{ width: 46, height: 46, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: isOpen ? "rgba(39,174,96,0.1)" : "rgba(25,36,47,0.06)", color: isOpen ? "success.main" : ERP_BRAND.muted }}>
                        <PointOfSaleRoundedIcon />
                      </Box>
                    </Stack>

                    {isOpen ? (
                      <Box sx={{ mt: 2.2, p: 2, bgcolor: ERP_BRAND.soft, borderRadius: 2.5 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>INICIAL</Typography>
                            <Typography sx={{ fontWeight: 900 }}>{formatMoney(item.openSession.openingAmount, register.currency)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>INGRESOS</Typography>
                            <Typography sx={{ fontWeight: 900, color: "success.main" }}>{formatMoney(item.totals?.incoming, register.currency)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>ESPERADO</Typography>
                            <Typography sx={{ fontWeight: 950 }}>{formatMoney(item.totals?.expected, register.currency)}</Typography>
                          </Grid>
                        </Grid>
                        <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 1.2 }}>
                          Abierta {formatDateTime(item.openSession.openedAt)} · egresos {formatMoney(item.totals?.outgoing, register.currency)}
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2.5 }}>La caja debe abrirse antes de registrar movimientos físicos.</Alert>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 2.2 }} flexWrap="wrap" useFlexGap>
                      {!isOpen ? (
                        <Button variant="contained" startIcon={<LockOpenRoundedIcon />} onClick={() => { setOpenTarget(item); setOpenForm(initialOpen); }} sx={{ textTransform: "none", fontWeight: 850 }}>Abrir caja</Button>
                      ) : (
                        <>
                          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setOperationTarget(item); setOperationForm(newOperationForm()); }} sx={{ textTransform: "none", fontWeight: 850 }}>Registrar operación</Button>
                          <Button variant="outlined" color="warning" startIcon={<LockRoundedIcon />} onClick={() => { setCloseTarget(item); setCloseForm({ ...initialClose, declaredClosingAmount: String(item.totals?.expected || 0) }); }} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </ErpPanel>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} xl={7.5}>
          <ErpPanel>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Movimientos de caja</Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>Ingresos y egresos asociados a sesiones físicas.</Typography>
            </Box>
            {!cashMovements.length ? <ErpEmpty title="Sin movimientos de caja" description="Los cobros, pagos e ingresos directos aparecerán aquí." /> : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fecha / número</TableCell>
                        <TableCell>Caja</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Referencia</TableCell>
                        <TableCell align="right">Importe</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cashMovements.map((movement) => (
                        <TableRow key={entityId(movement)} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 850 }}>{movement.cashMovementNumber}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
                          </TableCell>
                          <TableCell>{movement.registerId?.name || "—"}</TableCell>
                          <TableCell>{movement.description || "—"}</TableCell>
                          <TableCell>{movement.reference || "—"}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 950, color: movement.direction === "in" ? "success.main" : movement.direction === "out" ? "error.main" : ERP_BRAND.dark }}>
                            {movement.direction === "in" ? "+" : movement.direction === "out" ? "-" : ""}{formatMoney(movement.amount, movement.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={movementsTotal}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
                  rowsPerPageOptions={[10, 15, 25, 50]}
                  labelRowsPerPage="Filas por página"
                />
              </>
            )}
          </ErpPanel>
        </Grid>

        <Grid item xs={12} xl={4.5}>
          <ErpPanel>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Últimas sesiones</Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>Historial de aperturas y cierres.</Typography>
            </Box>
            {!sessions.length ? <ErpEmpty title="Sin sesiones" description="Cuando abras una caja se generará la primera sesión." /> : (
              <Stack spacing={0} sx={{ px: 2.5 }}>
                {sessions.slice(0, 10).map((session, index) => (
                  <Box key={entityId(session)} sx={{ py: 1.8, borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none" }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{session.registerId?.name || "Caja"}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{session.sessionNumber} · {formatDateTime(session.openedAt)}</Typography>
                      </Box>
                      <Chip size="small" color={session.status === "open" ? "success" : "default"} label={session.status === "open" ? "Abierta" : "Cerrada"} />
                    </Stack>
                    {session.status === "closed" ? (
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Diferencia</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 900, color: Number(session.difference) === 0 ? "success.main" : "warning.main" }}>{formatMoney(session.difference, session.currency)}</Typography>
                      </Stack>
                    ) : null}
                  </Box>
                ))}
              </Stack>
            )}
          </ErpPanel>
        </Grid>
      </Grid>

      <Dialog open={registerDialog} onClose={saving ? undefined : () => setRegisterDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Nueva caja</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Código" required value={registerForm.code} onChange={(event) => setRegisterForm((current) => ({ ...current, code: event.target.value.toUpperCase(), accountCode: current.accountCode || `CASH_${event.target.value.toUpperCase()}` }))} helperText="Ej.: PRINCIPAL, SUCURSAL_2" />
            <TextField label="Nombre" required value={registerForm.name} onChange={(event) => setRegisterForm((current) => ({ ...current, name: event.target.value }))} />
            <TextField label="Cuenta contable" value={registerForm.accountCode} onChange={(event) => setRegisterForm((current) => ({ ...current, accountCode: event.target.value.toUpperCase() }))} helperText="Se creará automáticamente como cuenta de activo." />
            <TextField label="Sucursal o ubicación" value={registerForm.branchName} onChange={(event) => setRegisterForm((current) => ({ ...current, branchName: event.target.value }))} />
            <TextField label="Notas" multiline minRows={2} value={registerForm.notes} onChange={(event) => setRegisterForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRegisterDialog(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={createRegister} disabled={saving}>{saving ? "Creando..." : "Crear caja"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(openTarget)} onClose={saving ? undefined : () => setOpenTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Abrir {openTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField type="number" label="Saldo inicial" value={openForm.openingAmount} onChange={(event) => setOpenForm((current) => ({ ...current, openingAmount: event.target.value }))} />
            <TextField label="Notas de apertura" multiline minRows={3} value={openForm.notes} onChange={(event) => setOpenForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTarget(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<LockOpenRoundedIcon />} onClick={openRegister} disabled={saving}>{saving ? "Abriendo..." : "Abrir caja"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(closeTarget)} onClose={saving ? undefined : () => setCloseTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Cerrar {closeTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">Saldo esperado: <strong>{formatMoney(closeTarget?.totals?.expected, closeTarget?.register?.currency)}</strong></Alert>
            <TextField type="number" label="Saldo declarado" value={closeForm.declaredClosingAmount} onChange={(event) => setCloseForm((current) => ({ ...current, declaredClosingAmount: event.target.value }))} />
            <TextField label="Notas de cierre" multiline minRows={3} value={closeForm.notes} onChange={(event) => setCloseForm((current) => ({ ...current, notes: event.target.value }))} />
            <Alert severity={Number(closeForm.declaredClosingAmount || 0) === Number(closeTarget?.totals?.expected || 0) ? "success" : "warning"}>
              Diferencia estimada: <strong>{formatMoney(Number(closeForm.declaredClosingAmount || 0) - Number(closeTarget?.totals?.expected || 0), closeTarget?.register?.currency)}</strong>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseTarget(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color="warning" startIcon={<LockRoundedIcon />} onClick={closeRegister} disabled={saving}>{saving ? "Cerrando..." : "Cerrar y arquear"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(operationTarget)} onClose={saving ? undefined : () => setOperationTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Operación en {operationTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField select label="Tipo" value={operationForm.direction} onChange={(event) => setOperationForm((current) => ({ ...current, direction: event.target.value, counterAccountCode: event.target.value === "in" ? "OTHER_INCOME" : "OTHER_EXPENSE" }))}>
              <MenuItem value="in"><Stack direction="row" spacing={1} alignItems="center"><CallReceivedRoundedIcon color="success" fontSize="small" /> <span>Ingreso</span></Stack></MenuItem>
              <MenuItem value="out"><Stack direction="row" spacing={1} alignItems="center"><CallMadeRoundedIcon color="error" fontSize="small" /> <span>Egreso</span></Stack></MenuItem>
            </TextField>
            <TextField type="number" label="Importe" required value={operationForm.amount} onChange={(event) => setOperationForm((current) => ({ ...current, amount: event.target.value }))} />
            <TextField label="Descripción" required value={operationForm.description} onChange={(event) => setOperationForm((current) => ({ ...current, description: event.target.value }))} />
            <TextField select label="Contrapartida contable" value={operationForm.counterAccountCode} onChange={(event) => setOperationForm((current) => ({ ...current, counterAccountCode: event.target.value }))}>
              {operationAccounts.map((account) => <MenuItem key={account.code} value={account.code}>{account.code} · {account.name} ({ACCOUNT_TYPE_LABELS[account.type]})</MenuItem>)}
            </TextField>
            <TextField label="Referencia" value={operationForm.reference} onChange={(event) => setOperationForm((current) => ({ ...current, reference: event.target.value }))} />
            <TextField label="Notas" multiline minRows={2} value={operationForm.notes} onChange={(event) => setOperationForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOperationTarget(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" color={operationForm.direction === "in" ? "success" : "error"} onClick={recordOperation} disabled={saving}>{saving ? "Registrando..." : "Registrar operación"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
